import { useState, type FormEvent } from 'react';
import { Bot, Check, Download, FileUp, Pencil, Plus, PlugZap, RefreshCw, Trash2, Unplug, Upload } from 'lucide-react';
import { AsyncView, Button, Card, Checkbox, Chip, Dialog, Input, Notice, Select, Table, useToast } from '../ui';
import { Drawer } from '../ui/Drawer';
import { Kebab } from '../ui/Kebab';
import { api, ApiError } from '../lib/api';
import { useResource } from '../lib/useResource';
import { podeAtuar, useSessao } from '../lib/session';
import './configuracao.css';

interface Telefone { id: string; displayNumber: string; verifiedName: string | null; phoneNumberId: string }
interface Canal { id: string; wabaId: string; phoneNumbers: Telefone[] }
/** Linha da tabela: um NÚMERO, com o contexto da conta a que pertence. A
 *  distinção importa porque desconectar age sobre a CONTA, não sobre a linha. */
interface Linha extends Telefone { wabaId: string; canalId: string; irmaos: number }

/**
 * Números conectados.
 *
 * Não passa pelo molde de CRUD porque a hierarquia é de dois níveis: uma conta
 * WABA tem N números, e a tabela mostra números, não contas.
 *
 * O comentário anterior aqui dizia que conectar "não é um formulário, é o fluxo
 * de Embedded Signup da Meta" — e por isso o botão apenas navegava para
 * `/index.html#channels`, do front antigo. Estava errado em duas frentes.
 * Primeira: desde que `WEB_ROOT` virou `dist/web`, esse caminho cai no fallback
 * de SPA e recarrega esta mesma aplicação, então o único botão da tela não
 * levava a lugar nenhum. Segunda: existem DOIS caminhos no servidor, e o
 * manual (`POST /workspace/channels`) é exatamente um formulário de cinco
 * campos. O Embedded Signup continua faltando — é outro trabalho, maior — mas
 * a ausência dele não justificava a tela não ter nenhuma forma de conectar.
 */
export function Numeros() {
  const { agent } = useSessao();
  const toast = useToast();
  const podeGerir = podeAtuar(agent.role, 'ADMIN');
  const { state, recarregar } = useResource<Canal[]>('/workspace/channels');
  const [conectando, setConectando] = useState(false);
  const [desconectando, setDesconectando] = useState<Linha | null>(null);

  const linhas: Linha[] = state.status === 'ready'
    ? state.data.flatMap((canal) => canal.phoneNumbers.map((tel) => ({
        ...tel,
        wabaId: canal.wabaId,
        canalId: canal.id,
        irmaos: canal.phoneNumbers.length,
      })))
    : [];

  const botao = podeGerir ? (
    <Button variant="primary" icon={<Plus size={18} aria-hidden="true" />} onClick={() => setConectando(true)}>
      Conectar número
    </Button>
  ) : undefined;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      {botao && state.status === 'ready' && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>{botao}</div>
      )}
      <Card>
        <AsyncView
          state={state}
          label="os números"
          empty={{
            title: 'Nenhum número conectado',
            text: podeGerir
              ? 'Conecte a conta WABA e o número do WhatsApp Business para começar a receber mensagens.'
              : 'Peça a um administrador do workspace para conectar um número do WhatsApp Business.',
            action: botao,
          }}
        >
          {() => (
            <Table
              caption="Números conectados"
              rowKey={(t) => t.id}
              rows={linhas}
              columns={[
                { key: 'numero', header: 'Número', cell: (t) => t.displayNumber },
                { key: 'nome', header: 'Nome verificado', cell: (t) => t.verifiedName ?? '—' },
                { key: 'waba', header: 'Conta WABA', cell: (t) => t.wabaId },
                ...(podeGerir ? [{
                  key: 'acoes', header: 'Ações', align: 'end' as const,
                  cell: (t: Linha) => (
                    <Kebab
                      rotulo={t.displayNumber}
                      acoes={[{
                        rotulo: 'Desconectar conta WABA',
                        icone: <Unplug size={16} aria-hidden="true" />,
                        perigo: true,
                        onSelect: () => setDesconectando(t),
                      }]}
                    />
                  ),
                }] : []),
              ]}
            />
          )}
        </AsyncView>
      </Card>

      <FormularioCanal
        aberto={conectando}
        onFechar={() => setConectando(false)}
        onConectado={() => { setConectando(false); recarregar(); }}
      />

      <ConfirmarDesconexao
        linha={desconectando}
        onFechar={() => setDesconectando(null)}
        onDesconectado={(numero) => {
          setDesconectando(null);
          toast(`A conta WABA de ${numero} foi desconectada.`);
          recarregar();
        }}
      />
    </div>
  );
}

/**
 * Desconectar conta WABA.
 *
 * Este é o botão de maior alcance da aplicação e a linha da tabela esconde
 * isso: o endpoint é `DELETE /workspace/channels/:id`, onde `:id` é a **conta
 * WABA**, não o número clicado. O `onDelete: Cascade` do schema vai até o fim —
 * conta → números → conversas → mensagens e notas — e leva junto os modelos
 * sincronizados. Apagar "um número" apaga o histórico de atendimento inteiro
 * daquele canal.
 *
 * Por isso a confirmação exige digitar o identificador da conta. Não é
 * cerimônia: é o único jeito de garantir que a pessoa leu QUAL conta vai
 * embora, num diálogo aberto a partir de uma linha que mostra outra coisa.
 */
function ConfirmarDesconexao({
  linha, onFechar, onDesconectado,
}: { linha: Linha | null; onFechar: () => void; onDesconectado: (numero: string) => void }) {
  const [confirmacao, setConfirmacao] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [apagando, setApagando] = useState(false);

  async function desconectar() {
    if (!linha) return;
    setErro(null);
    setApagando(true);
    try {
      await api(`/workspace/channels/${linha.canalId}`, { method: 'DELETE' });
      setConfirmacao('');
      onDesconectado(linha.displayNumber);
    } catch (falha) {
      setErro(falha instanceof ApiError ? falha.message : 'Falha de conexão.');
    } finally {
      setApagando(false);
    }
  }

  return (
    <Dialog
      open={linha !== null}
      onClose={() => { setConfirmacao(''); setErro(null); onFechar(); }}
      title="Desconectar conta WABA"
      footer={
        <>
          <Button variant="secondary" onClick={() => { setConfirmacao(''); setErro(null); onFechar(); }}>Cancelar</Button>
          <Button
            variant="danger"
            loading={apagando}
            disabled={confirmacao.trim() !== linha?.wabaId}
            icon={<Unplug size={18} aria-hidden="true" />}
            onClick={() => void desconectar()}
          >
            Desconectar definitivamente
          </Button>
        </>
      }
    >
      {linha && (
        <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
          {erro && <Notice tone="danger" title="Não foi possível desconectar" live>{erro}</Notice>}
          <Notice tone="danger" title="Isto apaga muito mais do que um número">
            <p style={{ margin: 0 }}>
              A conta WABA <strong>{linha.wabaId}</strong> sai junto com{' '}
              {linha.irmaos === 1 ? 'o número dela' : `os ${linha.irmaos} números dela`}, todas as conversas e
              mensagens recebidas por esses números, as notas internas e os modelos sincronizados.
              Não há como desfazer.
            </p>
          </Notice>
          <Input
            label="Digite o identificador da conta para confirmar"
            value={confirmacao}
            onChange={(e) => setConfirmacao(e.currentTarget.value)}
            autoComplete="off"
            hint={`Digite exatamente: ${linha.wabaId}`}
          />
        </div>
      )}
    </Dialog>
  );
}

/**
 * Conectar número manualmente.
 *
 * Os cinco campos são os do schema do servidor, sem invenção: identificador da
 * conta WABA, do negócio, token de acesso, identificador do número e o PIN de
 * verificação em duas etapas.
 *
 * O token é `type="password"` e `autoComplete="off"`. Não é senha de ninguém —
 * é uma credencial permanente da conta da Meta, que costuma ser preenchida com
 * alguém olhando a tela ao lado, e que o gerenciador de senhas não deve
 * guardar como se fosse login deste produto. O servidor cifra e nunca devolve
 * o valor; a listagem não tem coluna para ele.
 */
function FormularioCanal({
  aberto, onFechar, onConectado,
}: { aberto: boolean; onFechar: () => void; onConectado: () => void }) {
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  async function enviar(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const dados = new FormData(event.currentTarget);
    setErro(null);
    setSalvando(true);
    try {
      await api('/workspace/channels', {
        method: 'POST',
        body: {
          wabaId: String(dados.get('wabaId') ?? '').trim(),
          businessId: String(dados.get('businessId') ?? '').trim(),
          accessToken: String(dados.get('accessToken') ?? '').trim(),
          phoneNumberId: String(dados.get('phoneNumberId') ?? '').trim(),
          pin: String(dados.get('pin') ?? '').trim(),
        },
      });
      onConectado();
    } catch (falha) {
      setErro(falha instanceof ApiError ? falha.message : 'Falha de conexão.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Drawer
      open={aberto}
      onClose={onFechar}
      title="Conectar número"
      description="Os identificadores e o token saem do Gerenciador de Negócios da Meta, em WhatsApp › Configuração da API."
      footer={
        <>
          <Button variant="tertiary" onClick={onFechar}>Cancelar</Button>
          <Button
            variant="primary"
            type="submit"
            form="form-canal"
            loading={salvando}
            loadingLabel="Conectando o número"
            icon={<PlugZap size={18} aria-hidden="true" />}
          >
            Conectar
          </Button>
        </>
      }
    >
      <form id="form-canal" onSubmit={enviar} style={{ display: 'grid', gap: 'var(--space-5)' }}>
        {erro && <Notice tone="danger" title="Não foi possível conectar" live>{erro}</Notice>}
        <Input label="ID da conta WABA" name="wabaId" required minLength={2} maxLength={80}
          hint="Só dígitos, como aparece no Gerenciador. Um ID não numérico é aceito apenas em ambiente local." />
        <Input label="ID do negócio" name="businessId" required minLength={2} maxLength={80} />
        <Input label="ID do número" name="phoneNumberId" required minLength={2} maxLength={80}
          hint="É o identificador do número na API, não o telefone em si." />
        <Input label="Token de acesso" name="accessToken" type="password" required minLength={8}
          autoComplete="off"
          hint="Token permanente do aplicativo. É cifrado ao salvar e nunca mais exibido." />
        <Input label="PIN de verificação" name="pin" required inputMode="numeric" pattern="\d{6}"
          hint="Os seis dígitos do PIN de verificação em duas etapas do número." />
      </form>
    </Drawer>
  );
}

interface Arquivo {
  id: string; originalName: string; mimeType: string; sizeBytes: number;
  indexStatus: string; createdAt: string; uploadedBy: { name: string } | null; _count?: { chunks: number };
}
interface RespostaArquivos { files: Arquivo[]; limits: { maxBytes: number }; dedicatedEncryptionKey: boolean }

const tamanho = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
};

const INDEX: Record<string, { rotulo: string; tom: 'success' | 'warning' | 'danger' | 'neutral' }> = {
  READY: { rotulo: 'Indexado', tom: 'success' },
  PROCESSING: { rotulo: 'Processando', tom: 'warning' },
  FAILED: { rotulo: 'Falhou', tom: 'danger' },
  PENDING: { rotulo: 'Na fila', tom: 'neutral' },
};

/**
 * Biblioteca de arquivos.
 *
 * A resposta não é um array: é `{ files, limits, dedicatedEncryptionKey }`. Por
 * isso `vazio` precisa ser explícito — o padrão do hook só sabe reconhecer
 * array vazio, e sem isto uma biblioteca sem arquivos cairia em `ready` com
 * uma tabela de zero linhas, que é a tela em branco de sempre.
 */
export function Arquivos() {
  const { agent } = useSessao();
  const toast = useToast();
  const podeGerir = podeAtuar(agent.role, 'SUPERVISOR');
  const { state, recarregar } = useResource<RespostaArquivos>('/workspace/files', {
    vazio: (d) => d.files.length === 0,
  });
  const [enviando, setEnviando] = useState(false);
  const [excluindo, setExcluindo] = useState<Arquivo | null>(null);
  const [apagando, setApagando] = useState(false);

  const maxBytes = state.status === 'ready' ? state.data.limits.maxBytes : 0;

  async function reindexar(f: Arquivo) {
    try {
      await api(`/workspace/files/${f.id}/index`, { method: 'POST' });
      toast(`${f.originalName} entrou na fila de indexação.`);
      recarregar();
    } catch (erro) {
      toast(erro instanceof ApiError ? erro.message : 'Falha de conexão.', 'danger');
    }
  }

  async function excluir() {
    if (!excluindo) return;
    setApagando(true);
    try {
      await api(`/workspace/files/${excluindo.id}`, { method: 'DELETE' });
      toast(`${excluindo.originalName} foi removido da biblioteca.`);
      setExcluindo(null);
      recarregar();
    } catch (erro) {
      toast(erro instanceof ApiError ? erro.message : 'Falha ao excluir.', 'danger');
    } finally {
      setApagando(false);
    }
  }

  const botao = podeGerir ? (
    <Button variant="primary" icon={<Upload size={18} aria-hidden="true" />} onClick={() => setEnviando(true)}>
      Enviar arquivo
    </Button>
  ) : undefined;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      {state.status === 'ready' && !state.data.dedicatedEncryptionKey && (
        <Notice tone="warning" title="Sem chave dedicada">
          Os arquivos estão cifrados com a chave padrão da instalação. Configure uma chave por workspace antes de subir documento sensível.
        </Notice>
      )}
      <Card actions={state.status === 'ready' ? botao : undefined}>
        <AsyncView
          state={state}
          label="os arquivos"
          empty={{
            title: 'Biblioteca vazia',
            text: podeGerir
              ? 'Arquivos enviados aqui ficam disponíveis para os agentes de IA consultarem durante o atendimento.'
              : 'Peça a um gestor para enviar os documentos que os agentes de IA devem consultar.',
            action: botao,
          }}
        >
          {(d) => (
            <Table
              caption="Arquivos da biblioteca"
              rowKey={(f) => f.id}
              rows={d.files}
              columns={[
                /* Baixar é LINK, não item de menu: é navegação para um recurso
                   com endereço próprio, e como link ganha de graça abrir em
                   nova aba, copiar endereço e o menu de contexto do navegador —
                   tudo que um <button> com `location.assign` destrói. */
                { key: 'nome', header: 'Arquivo', cell: (f) => (
                  <a className="link-arquivo" href={`/api/workspace/files/${f.id}/download`} download={f.originalName}>
                    <Download size={14} aria-hidden="true" />
                    {f.originalName}
                  </a>
                ) },
                { key: 'tamanho', header: 'Tamanho', cell: (f) => tamanho(f.sizeBytes) },
                { key: 'indice', header: 'Indexação', cell: (f) => {
                  const i = INDEX[f.indexStatus] ?? { rotulo: f.indexStatus, tom: 'neutral' as const };
                  return <Chip tone={i.tom}>{i.rotulo}</Chip>;
                } },
                { key: 'trechos', header: 'Trechos', cell: (f) => String(f._count?.chunks ?? 0) },
                { key: 'quem', header: 'Enviado por', cell: (f) => f.uploadedBy?.name ?? '—' },
                ...(podeGerir ? [{
                  key: 'acoes', header: 'Ações', align: 'end' as const,
                  cell: (f: Arquivo) => (
                    <Kebab
                      rotulo={f.originalName}
                      acoes={[
                        {
                          rotulo: 'Indexar novamente',
                          icone: <RefreshCw size={16} aria-hidden="true" />,
                          onSelect: () => void reindexar(f),
                          desabilitado: f.indexStatus === 'PROCESSING',
                        },
                        {
                          rotulo: 'Excluir', icone: <Trash2 size={16} aria-hidden="true" />,
                          perigo: true, separar: true, onSelect: () => setExcluindo(f),
                        },
                      ]}
                    />
                  ),
                }] : []),
              ]}
            />
          )}
        </AsyncView>
      </Card>

      <FormularioArquivo
        aberto={enviando}
        maxBytes={maxBytes}
        onFechar={() => setEnviando(false)}
        onEnviado={(nome) => { setEnviando(false); toast(`${nome} entrou na biblioteca e está sendo indexado.`); recarregar(); }}
      />

      <Dialog
        open={excluindo !== null}
        onClose={() => setExcluindo(null)}
        title="Confirmar exclusão"
        footer={
          <>
            <Button variant="secondary" onClick={() => setExcluindo(null)}>Cancelar</Button>
            <Button variant="danger" loading={apagando} icon={<Trash2 size={18} aria-hidden="true" />} onClick={() => void excluir()}>
              Excluir definitivamente
            </Button>
          </>
        }
      >
        <p>
          {excluindo
            ? `O arquivo "${excluindo.originalName}" e os trechos indexados dele serão apagados. Automações que enviam este arquivo param de funcionar.`
            : ''}
        </p>
      </Dialog>
    </div>
  );
}

/**
 * Envio de arquivo.
 *
 * O endpoint recebe JSON com o conteúdo em base64, não `multipart`. Duas
 * consequências que o formulário precisa respeitar:
 *
 * 1. base64 cresce ~33%. A verificação de tamanho é feita sobre os BYTES do
 *    arquivo, contra o limite que a própria listagem informou — mandar 12 MB
 *    para descobrir no servidor que o teto é 10 custa a espera do upload
 *    inteiro antes do erro.
 * 2. O servidor confere a ASSINATURA do conteúdo contra o tipo declarado. Um
 *    `.pdf` que na verdade é um `.docx` renomeado é recusado lá — e a mensagem
 *    dele é melhor do que qualquer palpite que a tela pudesse dar.
 */
function FormularioArquivo({
  aberto, maxBytes, onFechar, onEnviado,
}: { aberto: boolean; maxBytes: number; onFechar: () => void; onEnviado: (nome: string) => void }) {
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  function escolher(lista: FileList | null) {
    const escolhido = lista?.[0] ?? null;
    setErro(null);
    if (escolhido && maxBytes && escolhido.size > maxBytes) {
      setArquivo(null);
      setErro(`O arquivo tem ${tamanho(escolhido.size)} e o limite é ${tamanho(maxBytes)}.`);
      return;
    }
    setArquivo(escolhido);
  }

  async function enviar(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!arquivo) { setErro('Escolha um arquivo.'); return; }
    setErro(null);
    setSalvando(true);
    try {
      const conteudo = await new Promise<string>((resolve, reject) => {
        const leitor = new FileReader();
        leitor.onerror = () => reject(new Error('Não foi possível ler o arquivo.'));
        // `readAsDataURL` devolve "data:<tipo>;base64,<conteúdo>"; o servidor
        // quer só o conteúdo.
        leitor.onload = () => resolve(String(leitor.result).split(',')[1] ?? '');
        leitor.readAsDataURL(arquivo);
      });
      await api('/workspace/files', {
        method: 'POST',
        body: {
          filename: arquivo.name,
          mimeType: arquivo.type || 'application/octet-stream',
          contentBase64: conteudo,
        },
      });
      const nome = arquivo.name;
      setArquivo(null);
      onEnviado(nome);
    } catch (falha) {
      setErro(falha instanceof ApiError ? falha.message : 'Falha de conexão.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Drawer
      open={aberto}
      onClose={onFechar}
      title="Enviar arquivo"
      description="Documentos da biblioteca são cifrados no armazenamento e indexados para consulta dos agentes de IA."
      footer={
        <>
          <Button variant="tertiary" onClick={onFechar}>Cancelar</Button>
          <Button
            variant="primary" type="submit" form="form-arquivo"
            loading={salvando} loadingLabel="Enviando o arquivo"
            icon={<FileUp size={18} aria-hidden="true" />}
          >
            Enviar
          </Button>
        </>
      }
    >
      <form id="form-arquivo" onSubmit={enviar} style={{ display: 'grid', gap: 'var(--space-5)' }}>
        {erro && <Notice tone="danger" title="Não foi possível enviar" live>{erro}</Notice>}
        <Input
          label="Arquivo"
          name="arquivo"
          type="file"
          required
          accept=".pdf,.txt,.md,.csv,.docx,.png,.jpg,.jpeg"
          onChange={(e) => escolher(e.currentTarget.files)}
          hint={maxBytes ? `Até ${tamanho(maxBytes)}. PDF, texto, CSV, Word ou imagem.` : 'PDF, texto, CSV, Word ou imagem.'}
        />
        {arquivo && (
          <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
            {arquivo.name} · {tamanho(arquivo.size)}
          </p>
        )}
      </form>
    </Drawer>
  );
}

interface Integracao { id: string; name: string; provider: string; kind: string; enabled: boolean; hasSecret: boolean; hasOutboundSecret?: boolean; config?: unknown }
interface AgenteVinculado { key: string; name: string; enabled: boolean; isEntryPoint: boolean }
interface EventoCrm { id:string;type:string;status:string;error:string|null;receivedAt:string;conversationId:string|null;contact:{waId:string;profileName:string|null}|null;integration:{name:string} }

const TIPO: Record<string, string> = { AI_AGENT: 'Agente de IA', FINANCE_SYSTEM: 'Financeiro', CRM: 'CRM' };

export function Integracoes({ onOpenAgents }: { onOpenAgents: () => void }) {
  const { agent } = useSessao();
  const toast = useToast();
  const podeGerir = podeAtuar(agent.role, 'ADMIN');
  const { state, recarregar } = useResource<Integracao[]>('/integrations', {
    enabled: podeGerir,
    disabledReason: 'Integrações guardam credenciais e são visíveis apenas para administradores.',
  });
  const agentes = useResource<AgenteVinculado[]>('/ai-agents', {
    enabled: podeGerir,
    disabledReason: 'Agentes de IA são visíveis apenas para administradores.',
  });
  const eventosCrm=useResource<EventoCrm[]>('/integrations/crm/events',{enabled:podeGerir});
  const [criando, setCriando] = useState<string | null>(null);
  const [editando, setEditando] = useState<Integracao | null>(null);
  const [excluindo, setExcluindo] = useState<Integracao | null>(null);
  const [apagando, setApagando] = useState(false);
  const [crmAberto,setCrmAberto]=useState(false);
  const [crmSecret,setCrmSecret]=useState<{secret:string;endpoint:string}|null>(null);
  const [crmApiKey,setCrmApiKey]=useState<Integracao|null>(null);

  async function excluir() {
    if (!excluindo) return;
    setApagando(true);
    try {
      await api(`/integrations/${excluindo.id}`, { method: 'DELETE' });
      toast(`${excluindo.name} foi removida.`);
      setExcluindo(null);
      recarregar();
    } catch (erro) {
      toast(erro instanceof ApiError ? erro.message : 'Falha ao excluir.', 'danger');
    } finally {
      setApagando(false);
    }
  }

  async function rotacionarCrm(integracao:Integracao){
    try{const resultado=await api<{secret:string}>(`/integrations/crm/${integracao.id}/rotate-secret`,{method:'POST'});setCrmSecret({secret:resultado.secret,endpoint:'/api/events/inbound'});setCrmAberto(true);toast('Segredo rotacionado. Atualize o CRM antes do próximo evento.');}
    catch(error){toast(error instanceof ApiError?error.message:'Falha ao rotacionar o segredo.','danger');}
  }

  const botao = podeGerir ? (
    <Button variant="primary" icon={<Plus size={18} aria-hidden="true" />} onClick={() => setCriando('')}>
      Nova integração
    </Button>
  ) : undefined;

  return (
    <div className="integracoes">
    <section aria-labelledby="catalogo-provedores">
      <div className="integracoes__cabecalho">
        <div>
          <h2 id="catalogo-provedores">Provedores</h2>
          <p>Escolha uma ferramenta para iniciar a conexão. A credencial será cifrada pelo servidor.</p>
        </div>
      </div>
      <div className="provedores" role="list">
        {PROVEDORES_CATALOGO.map((provedor) => (
          <article className="provedor" data-provider={provedor.value} role="listitem" key={provedor.value}>
            <span className="provedor__marca" aria-hidden="true">{provedor.mark}</span>
            <div className="provedor__corpo">
              <h3>{provedor.label}</h3>
              <p>{provedor.description}</p>
            </div>
            {podeGerir && (
              <Button variant="secondary" size="sm" onClick={() => provedor.value==='LEAD_MACHINE'?setCrmAberto(true):setCriando(provedor.value)}>
                Conectar
              </Button>
            )}
          </article>
        ))}
      </div>
    </section>

    <Card
      title="Integrações conectadas"
      subtitle="Credenciais e destinos disponíveis para automações e agentes."
      actions={state.status === 'ready' ? botao : undefined}
    >
      <AsyncView
        state={state}
        label="as integrações"
        empty={{
          title: 'Nenhuma integração',
          text: 'Integrações conectam provedores de IA e sistemas financeiros ao atendimento. Sem uma integração de IA ativa, a ação "Consultar agente de IA" das automações não tem para onde ir.',
          action: botao,
        }}
      >
        {(linhas) => (
          <Table
            caption="Integrações configuradas"
            rowKey={(i) => i.id}
            rows={linhas}
            columns={[
              { key: 'nome', header: 'Integração', cell: (i) => i.name },
              { key: 'tipo', header: 'Tipo', cell: (i) => TIPO[i.kind] ?? i.kind },
              { key: 'provedor', header: 'Provedor', cell: (i) => i.provider },
              /* Duas informações que o front anterior fundia numa só coluna
                 "status": estar ligada e ter credencial são coisas diferentes,
                 e uma integração ligada SEM segredo é justamente o caso que
                 falha em silêncio na primeira chamada. */
              { key: 'estado', header: 'Estado', cell: (i) => i.enabled ? <Chip tone="success">Ativa</Chip> : <Chip>Desligada</Chip> },
              { key: 'credencial', header: 'Credencial', cell: (i) => i.kind==='CRM'
                ? <Chip tone={i.hasSecret&&i.hasOutboundSecret?'success':'warning'}>{i.hasSecret&&i.hasOutboundSecret?'Bidirecional':'Configuração pendente'}</Chip>
                : i.hasSecret ? <Chip tone="success">Configurada</Chip> : <Chip tone="danger">Ausente</Chip> },
              ...(podeGerir ? [{
                key: 'acoes', header: 'Ações', align: 'end' as const,
                cell: (i: Integracao) => (
                  <Kebab
                    rotulo={i.name}
                    acoes={[
                      { rotulo: 'Editar', icone: <Pencil size={16} aria-hidden="true" />, onSelect: () => setEditando(i) },
                      ...(i.kind==='CRM'?[{rotulo:'Configurar chave de API',icone:<PlugZap size={16} aria-hidden="true"/>,onSelect:()=>setCrmApiKey(i)}]:[]),
                      ...(i.kind==='CRM'?[{rotulo:'Rotacionar segredo',icone:<RefreshCw size={16} aria-hidden="true"/>,onSelect:()=>void rotacionarCrm(i)}]:[]),
                      { rotulo: 'Excluir', icone: <Trash2 size={16} aria-hidden="true" />, perigo: true, separar: true, onSelect: () => setExcluindo(i) },
                    ]}
                  />
                ),
              }] : []),
            ]}
          />
        )}
      </AsyncView>
    </Card>

    <Card title="Eventos recebidos do CRM" subtitle="Follow-ups encaminhados para o responsável atual da conversa.">
      <AsyncView state={eventosCrm.state} label="os eventos do CRM" empty={{title:'Nenhum evento recebido',text:'Depois de conectar o CRM, as entregas aparecerão aqui com o resultado do vínculo.'}}>
        {(linhas)=><Table caption="Eventos recebidos do CRM" rowKey={(e)=>e.id} rows={linhas} columns={[
          {key:'quando',header:'Recebido',cell:(e)=>new Date(e.receivedAt).toLocaleString('pt-BR')},
          {key:'integracao',header:'Integração',cell:(e)=>e.integration.name},
          {key:'contato',header:'Contato',cell:(e)=>e.contact?.profileName??e.contact?.waId??'—'},
          {key:'estado',header:'Estado',cell:(e)=><Chip tone={e.status==='NOTIFIED'?'success':e.status==='UNMATCHED'?'warning':'danger'}>{e.status==='NOTIFIED'?'Notificado':e.status==='UNMATCHED'?'Sem vínculo':e.status}</Chip>},
          {key:'detalhe',header:'Detalhe',cell:(e)=>e.error??(e.conversationId?'Conversa vinculada':'—')},
        ]}/>}
      </AsyncView>
    </Card>

    <Card
      title="Agentes vinculados"
      subtitle="Agentes do workspace que podem usar as integrações e ferramentas autorizadas."
      actions={podeGerir ? (
        <Button variant="secondary" icon={<Bot size={18} aria-hidden="true" />} onClick={onOpenAgents}>
          Gerenciar agentes
        </Button>
      ) : undefined}
    >
      <AsyncView
        state={agentes.state}
        label="os agentes vinculados"
        skeletonRows={2}
        empty={{
          title: 'Nenhum agente configurado',
          text: 'Crie um agente e autorize explicitamente as ferramentas que ele poderá utilizar.',
          action: podeGerir ? <Button variant="secondary" onClick={onOpenAgents}>Criar agente</Button> : undefined,
        }}
      >
        {(linhas) => (
          <div className="agentes-vinculados" role="list">
            {linhas.map((agente) => (
              <article className="agente-vinculado" role="listitem" key={agente.key}>
                <span className="agente-vinculado__icone" aria-hidden="true"><Bot size={18} /></span>
                <div>
                  <strong>{agente.name}</strong>
                  <span>{agente.key}</span>
                </div>
                {agente.isEntryPoint && <Chip tone="brand">Ponto de entrada</Chip>}
                <Chip tone={agente.enabled ? 'success' : 'neutral'}>{agente.enabled ? 'Ativo' : 'Inativo'}</Chip>
              </article>
            ))}
          </div>
        )}
      </AsyncView>
    </Card>

    <FormularioIntegracao
      key={`nova-${criando ?? 'fechada'}`}
      providerInicial={criando || undefined}
      aberto={criando !== null}
      onFechar={() => setCriando(null)}
      onSalvo={() => { setCriando(null); recarregar(); toast('Integração criada.'); }}
    />
    <FormularioCrm aberto={crmAberto} segredo={crmSecret} onFechar={()=>{setCrmAberto(false);setCrmSecret(null);}} onCriado={(resultado)=>{setCrmSecret(resultado);recarregar();eventosCrm.recarregar();}} />
    <FormularioChaveCrm integracao={crmApiKey} onFechar={()=>setCrmApiKey(null)} onSalvo={()=>{setCrmApiKey(null);recarregar();toast('Chave de API do Lead Machine configurada.');}} />
    {/* `key` na INSTÂNCIA, não no <form>: `tipo` e `modo` são estado do
        componente, e sem remontar eles atravessam de uma linha para outra —
        abrir uma integração financeira e depois uma de IA mostraria os campos
        do financeiro. */}
    <FormularioIntegracao
      key={editando?.id ?? 'nenhuma'}
      integracao={editando}
      aberto={editando !== null}
      onFechar={() => setEditando(null)}
      onSalvo={() => { setEditando(null); recarregar(); toast('Alterações salvas.'); }}
    />

    <Dialog
      open={excluindo !== null}
      onClose={() => setExcluindo(null)}
      title="Confirmar exclusão"
      footer={
        <>
          <Button variant="secondary" onClick={() => setExcluindo(null)}>Cancelar</Button>
          <Button variant="danger" loading={apagando} icon={<Trash2 size={18} aria-hidden="true" />} onClick={() => void excluir()}>
            Excluir definitivamente
          </Button>
        </>
      }
    >
      <p>
        {excluindo
          ? `A integração "${excluindo.name}" e a credencial dela serão apagadas. Automações que dependem dela param de responder.`
          : ''}
      </p>
    </Dialog>
    </div>
  );
}

const PROVEDORES_CATALOGO = [
  { value: 'LEAD_MACHINE', label: 'Lead Machine / CRM', mark: 'LM', description: 'Receba follow-ups e notifique o responsável pela conversa.' },
  { value: 'OPENAI', label: 'ChatGPT / OpenAI', mark: 'AI', description: 'Modelos multimodais para texto, fotos e documentos.' },
  { value: 'ANTHROPIC', label: 'Claude / Anthropic', mark: 'C', description: 'Agentes Claude com visão e leitura de documentos.' },
  { value: 'GOOGLE_GEMINI', label: 'Google Gemini', mark: 'G', description: 'Modelos multimodais e agentes do Google.' },
  { value: 'META_LLAMA', label: 'Meta Llama', mark: 'M', description: 'Modelos Llama por endpoint ou provedor compatível.' },
  { value: 'AZURE_OPENAI', label: 'Azure OpenAI', mark: 'Az', description: 'Modelos OpenAI hospedados e governados pela Azure.' },
  { value: 'CUSTOM', label: 'Outro provedor', mark: '+', description: 'Conecte um provedor ou endpoint personalizado.' },
] as const;

function FormularioCrm({aberto,segredo,onFechar,onCriado}:{aberto:boolean;segredo:{secret:string;endpoint:string}|null;onFechar:()=>void;onCriado:(value:{secret:string;endpoint:string})=>void}){
  const [salvando,setSalvando]=useState(false);const [erro,setErro]=useState<string|null>(null);
  async function enviar(event:FormEvent<HTMLFormElement>){event.preventDefault();const form=new FormData(event.currentTarget);setSalvando(true);setErro(null);try{const result=await api<{secret:string;endpoint:string}>('/integrations/crm',{method:'POST',body:{name:String(form.get('name')??'').trim(),producer:String(form.get('producer')??'').trim(),externalOrganizationId:String(form.get('externalOrganizationId')??'').trim(),apiKey:String(form.get('apiKey')??'').trim()}});onCriado(result);}catch(error){setErro(error instanceof ApiError?error.message:'Falha de conexão.');}finally{setSalvando(false);}}
  return <Drawer open={aberto} onClose={onFechar} title="Conectar CRM" description="Vincule a organização externa ao workspace e receba follow-ups na plataforma." footer={<><Button variant="tertiary" onClick={onFechar}>{segredo?'Concluir':'Cancelar'}</Button>{!segredo&&<Button variant="primary" type="submit" form="form-crm" loading={salvando} icon={<Check size={18}/>}>Gerar conexão</Button>}</>}>
    {segredo?<Notice tone="success" title="Copie o segredo agora"><p>Ele não será exibido novamente.</p><Input label="Endpoint" readOnly value={segredo.endpoint}/><Input label="Segredo HMAC" readOnly value={segredo.secret}/></Notice>:<form id="form-crm" onSubmit={enviar} style={{display:'grid',gap:'var(--space-5)'}}>{erro&&<Notice tone="danger" title="Não foi possível conectar">{erro}</Notice>}<Input label="Nome" name="name" required defaultValue="Lead Machine"/><Input label="Produtor" name="producer" required defaultValue="lead-machine" hint="Identificador enviado no envelope do evento."/><Input label="ID da organização no CRM" name="externalOrganizationId" required hint="Não use nome ou slug; informe o identificador estável do CRM."/><Input label="Chave de API do Lead Machine" name="apiKey" type="password" required hint="Use uma chave com a permissão contact:resolve. Ela será cifrada e não voltará pela API."/></form>}
  </Drawer>;
}

function FormularioChaveCrm({integracao,onFechar,onSalvo}:{integracao:Integracao|null;onFechar:()=>void;onSalvo:()=>void}){
  const [salvando,setSalvando]=useState(false);const [erro,setErro]=useState<string|null>(null);
  async function enviar(event:FormEvent<HTMLFormElement>){event.preventDefault();if(!integracao)return;const form=new FormData(event.currentTarget);setSalvando(true);setErro(null);try{await api(`/integrations/crm/${integracao.id}/api-key`,{method:'PUT',body:{apiKey:String(form.get('apiKey')??'').trim()}});onSalvo();}catch(error){setErro(error instanceof ApiError?error.message:'Falha ao salvar a chave.');}finally{setSalvando(false);}}
  return <Drawer open={integracao!==null} onClose={onFechar} title="Configurar chave do Lead Machine" description="Autoriza o WA a resolver o contato no CRM sem expor a credencial." footer={<><Button variant="tertiary" onClick={onFechar}>Cancelar</Button><Button variant="primary" type="submit" form="form-crm-api-key" loading={salvando}>Salvar chave</Button></>}>
    <form id="form-crm-api-key" onSubmit={enviar} style={{display:'grid',gap:'var(--space-5)'}}>{erro&&<Notice tone="danger" title="Não foi possível salvar">{erro}</Notice>}<Input label="Chave de API" name="apiKey" type="password" required hint="A chave precisa possuir contact:resolve e será armazenada cifrada."/></form>
  </Drawer>;
}

const PROVEDORES = [
  ...PROVEDORES_CATALOGO.map(({ value, label }) => ({ value, label })),
  { value: 'WEBHOOK', label: 'Webhook' },
  { value: 'SANDBOX', label: 'Sandbox (sem chamada externa)' },
];

/**
 * Integração: criar e editar no mesmo formulário.
 *
 * Três regras do servidor que a tela reproduz em vez de descobrir por erro:
 *
 * 1. `provider` e `kind` NÃO são editáveis. Trocar o provedor mantendo a
 *    credencial cifrada do anterior quebra a próxima chamada com um erro que
 *    não aponta para a causa — o servidor recusa, e aqui os campos ficam
 *    travados com a explicação ao lado, em vez de aceitarem uma alteração que
 *    seria descartada.
 * 2. `secret` ausente significa "manter o atual". Por isso o campo fica vazio
 *    na edição e só é enviado quando alguém digita algo: se fosse obrigatório,
 *    mudar o nome exigiria redigitar a chave de API, e o caminho fácil viraria
 *    colar a chave errada.
 * 3. A configuração é um objeto DIFERENTE por tipo, e o do financeiro é
 *    `strict` — qualquer chave a mais reprova o corpo inteiro. Por isso os
 *    campos trocam junto com o tipo e nada é montado "por via das dúvidas".
 */
function FormularioIntegracao({
  integracao, providerInicial, aberto, onFechar, onSalvo,
}: { integracao?: Integracao | null; providerInicial?: string; aberto: boolean; onFechar: () => void; onSalvo: () => void }) {
  const editando = Boolean(integracao);
  const [tipo, setTipo] = useState<string>(integracao?.kind ?? 'AI_AGENT');
  const [modo, setModo] = useState<string>('');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const config = (integracao?.config ?? {}) as { model?: string; mode?: string; url?: string; authHeader?: string };
  const tipoAtual = editando ? (integracao!.kind) : tipo;
  const modoAtual = modo || config.mode || (tipoAtual === 'FINANCE_SYSTEM' ? 'webhook' : 'openai');

  async function enviar(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const dados = new FormData(event.currentTarget);
    const texto = (campo: string) => String(dados.get(campo) ?? '').trim();
    setErro(null);
    setSalvando(true);
    try {
      const corpoConfig = tipoAtual === 'CRM'
        ? (integracao?.config as Record<string,unknown> ?? {})
        : tipoAtual === 'FINANCE_SYSTEM'
        ? {
            mode: modoAtual,
            ...(modoAtual === 'webhook' ? { url: texto('url') } : {}),
            ...(modoAtual === 'webhook' && texto('authHeader') ? { authHeader: texto('authHeader') } : {}),
          }
        : { mode: modoAtual, ...(texto('model') ? { model: texto('model') } : {}) };
      const segredo = texto('secret');

      if (editando) {
        await api(`/integrations/${integracao!.id}`, {
          method: 'PATCH',
          body: {
            name: texto('name'),
            enabled: dados.get('enabled') === 'on',
            config: corpoConfig,
            // Vazio = manter. Não é o mesmo que remover, que exige limpar a
            // credencial de propósito no servidor.
            ...(segredo ? { secret: segredo } : {}),
          },
        });
      } else {
        await api('/integrations', {
          method: 'POST',
          body: {
            name: texto('name'),
            kind: tipoAtual,
            provider: texto('provider'),
            config: corpoConfig,
            ...(segredo ? { secret: segredo } : {}),
          },
        });
      }
      onSalvo();
    } catch (falha) {
      setErro(falha instanceof ApiError ? falha.message : 'Falha de conexão.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Drawer
      open={aberto}
      onClose={onFechar}
      title={editando ? 'Editar integração' : 'Nova integração'}
      description={editando
        ? 'Tipo e provedor não mudam: provedor diferente é integração diferente.'
        : 'Conecta um provedor de IA ou um sistema financeiro ao atendimento.'}
      footer={
        <>
          <Button variant="tertiary" onClick={onFechar}>Cancelar</Button>
          <Button
            variant="primary" type="submit" form="form-integracao"
            loading={salvando} loadingLabel="Salvando a integração"
            icon={<Check size={18} aria-hidden="true" />}
          >
            {editando ? 'Salvar alterações' : 'Criar integração'}
          </Button>
        </>
      }
    >
      <form
        id="form-integracao"
        key={integracao?.id ?? 'nova'}
        onSubmit={enviar}
        style={{ display: 'grid', gap: 'var(--space-5)' }}
      >
        {erro && <Notice tone="danger" title="Não foi possível salvar" live>{erro}</Notice>}

        <Input label="Nome" name="name" required minLength={2} defaultValue={integracao?.name}
          placeholder="Agente de atendimento" />

        {editando ? (
          <Notice tone="neutral">
            {TIPO[integracao!.kind] ?? integracao!.kind} · {integracao!.provider}
          </Notice>
        ) : (
          <>
            <Select
              label="Tipo" name="kind" required value={tipo}
              onChange={(e) => { setTipo(e.currentTarget.value); setModo(''); }}
              options={[
                { value: 'AI_AGENT', label: 'Agente de IA — responde e classifica conversas' },
                { value: 'FINANCE_SYSTEM', label: 'Sistema financeiro — recebe notas processadas' },
              ]}
            />
            <Select label="Provedor" name="provider" required options={PROVEDORES} placeholder="Selecione"
              defaultValue={providerInicial} />
          </>
        )}

        {tipoAtual === 'CRM' ? (
          <Notice tone="neutral">O identificador externo é imutável. Para trocar a organização vinculada, remova esta conexão e crie outra.</Notice>
        ) : tipoAtual === 'FINANCE_SYSTEM' ? (
          <>
            <Select
              label="Destino" name="mode" required value={modoAtual}
              onChange={(e) => setModo(e.currentTarget.value)}
              options={[
                { value: 'webhook', label: 'Webhook — envia para uma URL sua' },
                { value: 'sandbox', label: 'Sandbox — não envia nada para fora' },
              ]}
            />
            {modoAtual === 'webhook' && (
              <>
                <Input label="URL de destino" name="url" type="url" required defaultValue={config.url}
                  hint="Precisa estar na lista de destinos financeiros permitidos do ambiente. O servidor recusa qualquer outra." />
                <Input label="Cabeçalho de autorização" name="authHeader" defaultValue={config.authHeader}
                  placeholder="Authorization"
                  hint="Nome do cabeçalho que leva a credencial. Em branco, usa Authorization." />
              </>
            )}
          </>
        ) : (
          <>
            <Select
              label="Modo" name="mode" required value={modoAtual}
              onChange={(e) => setModo(e.currentTarget.value)}
              options={[
                { value: 'openai', label: 'Chamada real ao provedor' },
                { value: 'demo', label: 'Demonstração — resposta fixa, sem custo' },
              ]}
            />
            <Input label="Modelo" name="model" defaultValue={config.model} placeholder="gpt-5-mini"
              hint="Em branco, usa o padrão do servidor." />
          </>
        )}

        {tipoAtual !== 'CRM' && <Input
          label={editando ? 'Nova credencial' : 'Credencial'}
          name="secret" type="password" autoComplete="off"
          hint={editando
            ? 'Em branco, mantém a credencial atual. O valor guardado nunca é exibido de volta.'
            : 'Chave de API do provedor. É cifrada ao salvar e nunca mais exibida.'}
        />}

        {editando && (
          <Checkbox name="enabled" defaultChecked={integracao!.enabled}>
            Integração ativa
          </Checkbox>
        )}
      </form>
    </Drawer>
  );
}
